// frontend/src/pages/P2PProfile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import p2pAxios from '../p2pAxios';
import { FaUserCircle, FaThumbsUp, FaThumbsDown, FaUserCheck, FaClock } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

// IMPORTANT:
// If your p2pAxios baseURL already includes "/api/p2p" then keep API_BASE as below.
// If your route is mounted like "/api/p2p/external-reputation", then API_BASE should be "/external-reputation".
// If your route is mounted like "/api/p2p/external-reputation-requests", then change accordingly.
const API_BASE = '/external-reputation';

const PLATFORM_OPTIONS = [
  { key: 'paxful', label: 'Paxful' },
  { key: 'binance', label: 'Binance P2P' },
  { key: 'noones', label: 'Noones' },
  { key: 'okx', label: 'OKX P2P' },
  { key: 'bybit', label: 'Bybit P2P' },
  { key: 'other', label: 'Other' },
];

const asNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};

const normalizeUrl = (u) => {
  const s = String(u || '').trim();
  if (!s) return '';
  if (!/^https?:\/\//i.test(s)) return `https://${s}`;
  return s;
};

const P2PProfile = () => {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // form/edit state (existing profile)
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');

  // ---------------------------
  // External Reputation Import UI
  // ---------------------------
  const [reqOpen, setReqOpen] = useState(false);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqErr, setReqErr] = useState('');
  const [reqOk, setReqOk] = useState('');

  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestsErr, setMyRequestsErr] = useState('');
  const [myRequests, setMyRequests] = useState([]);

  // request form fields
  const [platform, setPlatform] = useState('paxful');
  const [platformLabel, setPlatformLabel] = useState('');
  const [extUsername, setExtUsername] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [feedbackTotal, setFeedbackTotal] = useState('');
  const [positive, setPositive] = useState('');
  const [neutral, setNeutral] = useState('');
  const [negative, setNegative] = useState('');
  const [proofNote, setProofNote] = useState('');
  const [proofLinksText, setProofLinksText] = useState('');
  const [proofCode, setProofCode] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await p2pAxios.get('/user/p2p-profile');
      const p = res.data?.profile || null;
      setProfile(p);
      setUsername(p?.username || '');
      setBio(p?.bio || '');
      setEditing(!p || !p.username);
    } catch (err) {
      console.error('Failed to load profile', err);
      setProfile(null);
      setEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const loadMyRequests = async () => {
    setMyRequestsErr('');
    setMyRequestsLoading(true);
    try {
      const res = await p2pAxios.get(`${API_BASE}/my-requests`);
      const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setMyRequests(rows);
    } catch (err) {
      console.error('Failed to load external reputation requests', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load requests';
      setMyRequestsErr(msg);
      setMyRequests([]);
    } finally {
      setMyRequestsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // auto-load requests once profile screen opens (safe; does not depend on profile)
  useEffect(() => {
    loadMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    setFormErr('');
    const clean = (username || '').trim().toLowerCase();
    if (!USERNAME_RE.test(clean)) {
      setFormErr('Username must be 3–20 chars, lowercase letters/numbers/underscore only.');
      return;
    }
    setSaving(true);
    try {
      const payload = { username: clean, bio: (bio || '').trim() || undefined };
      const method = profile ? 'patch' : 'post';
      const res = await p2pAxios[method]('/user/p2p-profile', payload);
      const newProfile = res.data?.profile
        ? res.data.profile
        : (await p2pAxios.get('/user/p2p-profile')).data.profile;

      setProfile(newProfile);
      setUsername(newProfile?.username || clean);
      setBio(newProfile?.bio || payload.bio || '');
      setEditing(false);

      // ✅ NEW: If user was forced here (from interceptor), send them back
      try {
        const next = sessionStorage.getItem('p2p_next_after_username');
        if (next) {
          sessionStorage.removeItem('p2p_next_after_username');
          // navigate back to where they came from
          navigate(next, { replace: true });
        }
      } catch (e) {
        // no-op
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save';
      setFormErr(msg);
    } finally {
      setSaving(false);
    }
  };

  const hasPendingForPlatform = useMemo(() => {
    const plat = String(platform || '').toLowerCase();
    return myRequests.some((r) => String(r?.platform || '').toLowerCase() === plat && r?.status === 'pending');
  }, [myRequests, platform]);

  const resetRequestMessages = () => {
    setReqErr('');
    setReqOk('');
  };

  const submitImportRequest = async () => {
    resetRequestMessages();
    setReqLoading(true);

    try {
      const plat = String(platform || '').trim().toLowerCase();
      const url = normalizeUrl(profileUrl);

      if (!plat) {
        setReqErr('Please select platform.');
        setReqLoading(false);
        return;
      }
      if (!url) {
        setReqErr('Please enter your public profile URL.');
        setReqLoading(false);
        return;
      }

      const links = String(proofLinksText || '')
        .split(',')
        .map((x) => normalizeUrl(x.trim()))
        .filter(Boolean);

      const payload = {
        platform: plat,
        platformLabel: platformLabel ? String(platformLabel).trim() : undefined,
        username: extUsername ? String(extUsername).trim() : undefined,
        profileUrl: url,
        feedbackTotal: asNum(feedbackTotal),
        positive: asNum(positive),
        neutral: asNum(neutral),
        negative: asNum(negative),
        proofNote: proofNote ? String(proofNote).trim() : undefined,
        proofLinks: links,
        proofCode: proofCode ? String(proofCode).trim() : undefined,
      };

      const res = await p2pAxios.post(`${API_BASE}/request`, payload);

      if (res.data?.ok) {
        setReqOk('Request submitted! Our team will review it.');
        // refresh list
        await loadMyRequests();
        // optionally close form
        // setReqOpen(false);
      } else {
        setReqErr(res.data?.error || 'Failed to submit request.');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to submit request';
      setReqErr(msg);
    } finally {
      setReqLoading(false);
    }
  };

  const statusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved') {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-900';
    }
    if (s === 'rejected') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900';
    }
    return 'bg-amber-100 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900';
  };

  if (loading)
    return (
      <div className="text-center mt-10 text-lg font-semibold text-gray-600 dark:text-gray-300">
        Loading profile...
      </div>
    );

  return (
    <motion.div
      className="min-h-screen bg-white dark:bg-neutral-950 md:py-10 py-4 md:px-16 px-2 sm:px-6 lg:px-32 transition-colors"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Card */}
      <div className="max-w-5xl mx-auto bg-white dark:bg-neutral-900 md:p-8 p-3 md:rounded-2xl rounded-xl md:shadow-lg shadow border-t-4 border-blue-600 dark:border-blue-500 overflow-hidden transition-colors">
        {/* Header */}
        <div className="flex items-center gap-3 md:gap-6 md:mb-6 mb-3">
          <FaUserCircle className="md:text-6xl text-4xl text-blue-500 dark:text-blue-400" />
          <div>
            <h1 className="md:text-3xl text-xl font-bold text-blue-800 dark:text-blue-300">
              {profile?.username ? `@${profile.username}` : 'Set your P2P username'}
            </h1>
            {profile?.username && (
              <p className="text-green-600 dark:text-green-400 md:text-base text-xs">Online</p>
            )}
            {profile?.bio && (
              <p className="text-gray-600 dark:text-gray-300 mt-0.5 md:text-base text-sm">
                {profile.bio}
              </p>
            )}
          </div>
        </div>

        {/* Create / Edit form */}
        {editing && (
          <div className="md:mb-8 mb-4 border rounded-xl md:p-5 p-3 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-900 transition-colors">
            <h2 className="md:text-xl text-lg font-semibold text-blue-700 dark:text-blue-300 md:mb-3 mb-2">
              {profile ? 'Edit P2P Profile' : 'Create your P2P Profile'}
            </h2>

            <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 md:mb-1 mb-1">
              Username
            </label>
            <div className="flex items-center gap-2 md:mb-3 mb-2">
              <span className="text-gray-500 dark:text-gray-400 text-sm md:text-base">@</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                placeholder="your_name"
                className="flex-1 border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-gray-300 dark:border-neutral-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                maxLength={20}
              />
            </div>
            <p className="md:text-xs text-[11px] text-gray-500 dark:text-gray-400 md:mb-3 mb-2">
              3–20 characters, lowercase letters, numbers, and underscore only.
            </p>

            <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 md:mb-1 mb-1">
              Bio (optional)
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell traders a bit about you (max 200 chars)"
              className="w-full border rounded-lg px-3 md:py-2 py-1.5 md:mb-3 mb-2 text-sm bg-white dark:bg-neutral-800 border-gray-300 dark:border-neutral-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              maxLength={200}
              rows={3}
            />

            {formErr && (
              <div className="text-red-600 dark:text-red-400 text-sm md:mb-3 mb-2">{formErr}</div>
            )}

            <div className="flex md:gap-2 gap-2 md:flex-row flex-col">
              <button
                onClick={onSave}
                disabled={saving}
                className="bg-blue-600 dark:bg-blue-600/90 text-white md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500 disabled:opacity-60 md:text-sm text-sm transition-colors"
              >
                {saving ? 'Saving…' : profile ? 'Save Changes' : 'Create Profile'}
              </button>
              {profile && (
                <button
                  onClick={() => {
                    setEditing(false);
                    setFormErr('');
                    setUsername(profile.username || '');
                    setBio(profile.bio || '');
                  }}
                  className="bg-gray-200 dark:bg-neutral-800 text-gray-800 dark:text-gray-100 md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-gray-300 dark:hover:bg-neutral-700 md:text-sm text-sm border border-transparent dark:border-neutral-700 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Show “Edit profile” button if profile exists */}
        {!editing && profile && (
          <div className="md:mb-6 mb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <button
              onClick={() => setEditing(true)}
              className="bg-blue-600 dark:bg-blue-600/90 text-white md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500 md:text-sm text-sm transition-colors"
            >
              Edit Profile
            </button>

            <button
              onClick={() => {
                setReqOpen((v) => !v);
                resetRequestMessages();
              }}
              className="bg-emerald-600 dark:bg-emerald-600/90 text-white md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-500 md:text-sm text-sm transition-colors"
            >
              {reqOpen ? 'Close Import Feedback' : 'Import Feedback (Paxful/Binance/etc.)'}
            </button>
          </div>
        )}

        {/* If no profile yet and user closed the form */}
        {!editing && !profile && (
          <div className="md:mb-8 mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 md:p-4 p-3 rounded-lg transition-colors">
            <p className="text-amber-900 dark:text-amber-200 md:text-base text-sm">
              You haven’t set your P2P username yet. Click below to create your profile.
            </p>
            <button
              onClick={() => setEditing(true)}
              className="mt-2 bg-amber-600 dark:bg-amber-600/90 text-white md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-amber-700 dark:hover:bg-amber-500 md:text-sm text-sm transition-colors"
            >
              Set Username
            </button>
          </div>
        )}

        {/* ---------------------------
            Import feedback request panel
           --------------------------- */}
        {profile && reqOpen && (
          <div className="md:mb-8 mb-4 border rounded-xl md:p-5 p-3 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="md:text-xl text-lg font-semibold text-emerald-700 dark:text-emerald-300 md:mb-1 mb-1">
                  Import Feedback Request
                </h2>
                <p className="text-gray-600 dark:text-gray-300 md:text-sm text-xs">
                  Submit your public profile link + stats. Our team will verify and approve/reject.
                </p>
              </div>

              <button
                onClick={async () => {
                  resetRequestMessages();
                  await loadMyRequests();
                }}
                disabled={myRequestsLoading}
                className="shrink-0 bg-white dark:bg-neutral-900 border border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300 md:px-3 px-3 md:py-2 py-1.5 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 md:text-sm text-xs transition-colors disabled:opacity-60"
              >
                {myRequestsLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            {/* Form grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:mt-4 mt-3">
              <div>
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Platform
                </label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                >
                  {PLATFORM_OPTIONS.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
                {hasPendingForPlatform && (
                  <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                    You already have a pending request for this platform.
                  </p>
                )}
              </div>

              <div>
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Platform Label (optional)
                </label>
                <input
                  value={platformLabel}
                  onChange={(e) => setPlatformLabel(e.target.value)}
                  placeholder="e.g., Paxful / Binance P2P"
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  maxLength={60}
                />
              </div>

              <div>
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Username on that platform (optional)
                </label>
                <input
                  value={extUsername}
                  onChange={(e) => setExtUsername(e.target.value)}
                  placeholder="e.g., trader_123"
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  maxLength={80}
                />
              </div>

              <div>
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Public Profile URL <span className="text-red-500">*</span>
                </label>
                <input
                  value={profileUrl}
                  onChange={(e) => setProfileUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                />
              </div>

              <div className="md:col-span-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Total
                    </label>
                    <input
                      value={feedbackTotal}
                      onChange={(e) => setFeedbackTotal(e.target.value)}
                      inputMode="numeric"
                      placeholder="0"
                      className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Positive
                    </label>
                    <input
                      value={positive}
                      onChange={(e) => setPositive(e.target.value)}
                      inputMode="numeric"
                      placeholder="0"
                      className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Neutral
                    </label>
                    <input
                      value={neutral}
                      onChange={(e) => setNeutral(e.target.value)}
                      inputMode="numeric"
                      placeholder="0"
                      className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Negative
                    </label>
                    <input
                      value={negative}
                      onChange={(e) => setNegative(e.target.value)}
                      inputMode="numeric"
                      placeholder="0"
                      className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                    />
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  Tip: These are counts only. Your team will verify from the profile URL.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Proof Note (optional)
                </label>
                <textarea
                  value={proofNote}
                  onChange={(e) => setProofNote(e.target.value)}
                  placeholder="Anything that helps verification (e.g., where stats are visible)."
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  rows={3}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Proof Links (optional, comma-separated)
                </label>
                <input
                  value={proofLinksText}
                  onChange={(e) => setProofLinksText(e.target.value)}
                  placeholder="https://imgur.com/... , https://drive.google.com/..."
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block md:text-sm text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Proof Code (optional)
                </label>
                <input
                  value={proofCode}
                  onChange={(e) => setProofCode(e.target.value)}
                  placeholder="If you use a bio-code verification flow"
                  className="w-full border rounded-lg px-3 md:py-2 py-1.5 text-sm bg-white dark:bg-neutral-800 border-emerald-200 dark:border-emerald-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                />
              </div>
            </div>

            {reqErr && (
              <div className="mt-3 text-red-700 dark:text-red-300 text-sm">{reqErr}</div>
            )}
            {reqOk && (
              <div className="mt-3 text-emerald-800 dark:text-emerald-200 text-sm">{reqOk}</div>
            )}
            {myRequestsErr && (
              <div className="mt-3 text-amber-800 dark:text-amber-200 text-sm">
                Requests: {myRequestsErr}
              </div>
            )}

            <div className="mt-4 flex gap-2 flex-col md:flex-row">
              <button
                onClick={submitImportRequest}
                disabled={reqLoading || hasPendingForPlatform}
                className="bg-emerald-600 dark:bg-emerald-600/90 text-white md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-500 disabled:opacity-60 md:text-sm text-sm transition-colors"
              >
                {reqLoading ? 'Submitting…' : hasPendingForPlatform ? 'Pending request exists' : 'Submit Request'}
              </button>

              <button
                onClick={() => {
                  setReqOpen(false);
                  resetRequestMessages();
                }}
                className="bg-gray-200 dark:bg-neutral-800 text-gray-800 dark:text-gray-100 md:px-4 px-3 md:py-2 py-1.5 rounded-lg hover:bg-gray-300 dark:hover:bg-neutral-700 md:text-sm text-sm border border-transparent dark:border-neutral-700 transition-colors"
              >
                Close
              </button>
            </div>

            {/* My Requests list */}
            <div className="mt-5">
              <h3 className="md:text-lg text-base font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                Your Import Requests
              </h3>

              {myRequestsLoading ? (
                <div className="text-sm text-gray-600 dark:text-gray-300">Loading requests…</div>
              ) : myRequests.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  No requests yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {myRequests.map((r) => (
                    <div
                      key={r._id}
                      className="border rounded-xl p-3 bg-white dark:bg-neutral-900 border-emerald-100 dark:border-emerald-900"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm md:text-base">
                            {String(r.platform || '').toUpperCase()}
                            {r.username ? <span className="text-gray-500"> · {r.username}</span> : null}
                          </div>
                          {r.profileUrl ? (
                            <a
                              href={r.profileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs md:text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                            >
                              {r.profileUrl}
                            </a>
                          ) : null}
                          <div className="mt-1 text-[11px] md:text-xs text-gray-600 dark:text-gray-300">
                            Total: <strong>{r.feedbackTotal || 0}</strong> · +{r.positive || 0} / 0{r.neutral || 0} / -{r.negative || 0}
                          </div>
                        </div>

                        <div className={`shrink-0 border px-2 py-1 rounded-lg text-[11px] md:text-xs ${statusBadge(r.status)}`}>
                          {String(r.status || 'pending').toUpperCase()}
                        </div>
                      </div>

                      {(r.adminNote || r.reviewedAt) && (
                        <div className="mt-2 text-[11px] md:text-xs text-gray-700 dark:text-gray-300">
                          {r.reviewedAt ? (
                            <div>
                              Reviewed:{' '}
                              <strong>
                                {new Date(r.reviewedAt).toLocaleString()}
                              </strong>
                            </div>
                          ) : null}
                          {r.adminNote ? (
                            <div className="mt-1">
                              Admin note: <span className="font-medium">{r.adminNote}</span>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats & badges – show only if we have a profile */}
        {profile && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:gap-6 gap-2.5 md:mb-10 mb-5">
              <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 md:p-4 p-2.5 rounded-lg text-[12px] md:text-base transition-colors">
                <FaThumbsUp className="inline mr-2" />
                Positive Feedback: <strong>+{profile.feedback?.positive || 0}</strong>
              </div>
              <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 md:p-4 p-2.5 rounded-lg text-[12px] md:text-base transition-colors">
                <FaThumbsDown className="inline mr-2" />
                Negative Feedback: <strong>-{profile.feedback?.negative || 0}</strong>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 md:p-4 p-2.5 rounded-lg text-[12px] md:text-base transition-colors">
                <FaClock className="inline mr-2" />
                Joined: <strong>{profile.joined || 'N/A'}</strong>
              </div>
              <div className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 md:p-4 p-2.5 rounded-lg col-span-1 md:col-span-2 text-[12px] md:text-base transition-colors">
                <p>
                  📞 <strong>Phone Verified</strong> | 📧 <strong>Email Verified</strong> | 🪪{' '}
                  <strong>ID Verified</strong> | 🏠 <strong>Address Verified</strong>
                </p>
                <p className="mt-0.5 md:mt-1">🚀 <strong>Power Trader</strong></p>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 md:p-4 p-2.5 rounded-lg text-[12px] md:text-base transition-colors">
                <FaUserCheck className="inline mr-2" /> Trusted By:{' '}
                <strong>{profile.trustedBy || 0}</strong>
              </div>
            </div>

            <div className="md:mb-8 mb-4">
              <h2 className="md:text-xl text-lg font-semibold text-blue-700 dark:text-blue-300 md:mb-4 mb-2.5 transition-colors">
                📊 Trading Stats
              </h2>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-200 md:space-y-2 space-y-1 md:text-base text-sm">
                <li>Trade Partners: <strong>{profile.tradePartners || 0}</strong></li>
                <li>Total Trades: <strong>{profile.totalTrades || 0}</strong></li>
                <li>Volume (BTC): <strong>{profile.volumeBTC || 'less than 1 BTC'}</strong></li>
                <li>Volume (ETH): <strong>{profile.volumeETH || 'less than 10 ETH'}</strong></li>
                <li>Volume (USDT): <strong>{profile.volumeUSDT || 'between 0 and 100K USDT'}</strong></li>
              </ul>
            </div>
          </>
        )}

        <div className="text-center md:mt-10 mt-5 md:text-sm text-xs text-gray-400 dark:text-gray-500">
          Coinzetto P2P Profile • Secure Trading with Confidence
        </div>
      </div>
    </motion.div>
  );
};

export default P2PProfile;
